import React, { useEffect, useState } from "react";
import constant from "../../../../constants";
import { getPaymentAccount } from "../../../../util/bankUtils";
import { FormInput, FormSelect, SectionTitle } from "./FormField";
import { CopyButton } from "./CopyButton";
import {
  FaUniversity,
  FaAlipay,
  FaEdit,
  FaCheck,
  FaChevronRight,
  FaGift,
} from "react-icons/fa";

export const OrderPayoutBank = ({
  orderInfo,
  setOrderInfo,
  user,
  updateValue,
  loading,
}) => {
  const [selectedPayoutSource, setSelectedPayoutSource] = useState("custom");

  // Retrieve user accounts with fallback to legacy user fields
  const thAcc = (() => {
    const fromUtil = getPaymentAccount(user, "TH_BANK");
    if (fromUtil?.accNo || fromUtil?.name) return fromUtil;
    return {
      name: user?.bankName || user?.bank?.name || "",
      accName: user?.bankAccName || user?.bank?.accName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
      accNo: user?.bankAccNo || user?.bank?.accNo || "",
      currency: "THB",
    };
  })();

  const laoAcc = getPaymentAccount(user, "LAO_BANK");
  const alipayAcc = getPaymentAccount(user, "ALIPAY");

  // Recipient account when buying for someone else
  const buyForAcc = {
    name: orderInfo?.buyFor?.bank || "",
    accName:
      orderInfo?.buyFor?.accName ||
      `${orderInfo?.buyFor?.firstName || ""} ${orderInfo?.buyFor?.lastName || ""}`.trim(),
    accNo: orderInfo?.buyFor?.accNo || "",
    currency: "THB",
  };
  const hasBuyFor = Boolean(buyForAcc.accNo || buyForAcc.name);

  const currentOrderBank =
    orderInfo?.prizeBank?.accNo || orderInfo?.prizeBank?.name
      ? orderInfo.prizeBank
      : orderInfo?.bank || {};

  useEffect(() => {
    if (!orderInfo) return;

    const bankName = currentOrderBank?.name || "";
    const bankAccNo = currentOrderBank?.accNo || "";

    if (bankName && bankAccNo) {
      if (
        orderInfo?.isBuyForSomeoneElse &&
        buyForAcc.accNo &&
        bankName === buyForAcc.name &&
        bankAccNo === buyForAcc.accNo
      ) {
        setSelectedPayoutSource("buy_for");
      } else if (thAcc.accNo && bankName === thAcc.name && bankAccNo === thAcc.accNo) {
        setSelectedPayoutSource("profile_th");
      } else if (laoAcc.accNo && bankName === laoAcc.name && bankAccNo === laoAcc.accNo) {
        setSelectedPayoutSource("profile_lao");
      } else if (
        alipayAcc.accNo &&
        (bankName === "Alipay" || bankAccNo === alipayAcc.accNo)
      ) {
        setSelectedPayoutSource("profile_alipay");
      } else {
        setSelectedPayoutSource("custom");
      }
    } else {
      const pm = orderInfo?.paymentMethod || "";
      const cur = orderInfo?.paidCurrency || "";

      if (orderInfo?.isBuyForSomeoneElse && (buyForAcc.accNo || buyForAcc.name)) {
        setSelectedPayoutSource("buy_for");
      } else if ((pm === "LAO_BANK" || cur === "LAK") && (laoAcc.accNo || laoAcc.name)) {
        setSelectedPayoutSource("profile_lao");
      } else if ((pm === "ALIPAY" || cur === "CNY") && alipayAcc.accNo) {
        setSelectedPayoutSource("profile_alipay");
      } else if (thAcc.accNo || thAcc.name) {
        setSelectedPayoutSource("profile_th");
      } else {
        setSelectedPayoutSource("custom");
      }
    }
  }, [
    orderInfo,
    currentOrderBank?.name,
    currentOrderBank?.accNo,
    user,
    thAcc.accNo,
    thAcc.name,
    laoAcc.accNo,
    laoAcc.name,
    alipayAcc.accNo,
    buyForAcc.accNo,
    buyForAcc.name,
    orderInfo?.isBuyForSomeoneElse,
  ]);

  const handleSelectSource = async (source, targetAccount, isDisabled) => {
    if (isDisabled || loading) return;

    setSelectedPayoutSource(source);

    if (source === "custom") return;

    if (
      window.confirm(`ยืนยันการใช้ข้อมูลบัญชี "${targetAccount.name || source}" สำหรับออเดอร์นี้หรือไม่?`)
    ) {
      setOrderInfo((prev) => ({
        ...prev,
        bank: targetAccount,
        prizeBank: targetAccount,
      }));
      updateValue("bank", targetAccount);
      updateValue("prizeBank", targetAccount);
    }
  };

  const hasTh = Boolean(thAcc.accNo || thAcc.name);
  const hasLao = Boolean(laoAcc.accNo || laoAcc.name);
  const hasAlipay = Boolean(alipayAcc.accNo);

  const payoutOptions = [
    ...(orderInfo?.isBuyForSomeoneElse
      ? [
          {
            id: "buy_for",
            title: "บัญชีผู้รับ (ซื้อให้บุคคลอื่น)",
            icon: <FaGift className="text-white text-xs" />,
            iconBg: "bg-rose-500",
            hasData: hasBuyFor,
            account: buyForAcc,
            desc: hasBuyFor
              ? `${buyForAcc.name || "ธนาคาร"} • เลขบัญชี: ${buyForAcc.accNo || "-"} • ${buyForAcc.accName || "-"}`
              : "ยังไม่มีการระบุข้อมูลบัญชีธนาคารของผู้รับในคำสั่งซื้อนี้",
          },
        ]
      : []),
    {
      id: "profile_th",
      title: "บัญชีธนาคารไทย",
      icon: <FaUniversity className="text-white text-xs" />,
      iconBg: "bg-blue",
      hasData: hasTh,
      account: thAcc,
      desc: hasTh
        ? `${thAcc.name || "ธนาคารไทย"} • เลขบัญชี: ${thAcc.accNo || "-"} • ${thAcc.accName || "-"}`
        : "ยังไม่มีการผูกบัญชีธนาคารไทยในโปรไฟล์ผู้ใช้",
    },
    {
      id: "profile_lao",
      title: "บัญชีธนาคาร สปป.ลาว",
      icon: <FaUniversity className="text-white text-xs" />,
      iconBg: "bg-green",
      hasData: hasLao,
      account: laoAcc,
      desc: hasLao
        ? `${laoAcc.name || "ธนาคารลาว"} • เลขบัญชี: ${laoAcc.accNo || "-"} • ${laoAcc.accName || "-"}`
        : "ยังไม่มีการผูกบัญชีธนาคารลาวในโปรไฟล์ผู้ใช้",
    },
    {
      id: "profile_alipay",
      title: "บัญชี Alipay",
      icon: <FaAlipay className="text-white text-xs" />,
      iconBg: "bg-[#027AFF]",
      hasData: hasAlipay,
      account: { ...alipayAcc, name: "Alipay" },
      desc: hasAlipay
        ? `Alipay ID / เบอร์: ${alipayAcc.accNo || "-"} • ${alipayAcc.accName || "-"}`
        : "ยังไม่มีการผูกบัญชี Alipay ในโปรไฟล์ผู้ใช้",
    },
    {
      id: "custom",
      title: "กำหนดเอง / บัญชีบุคคลอื่น",
      icon: <FaEdit className="text-white text-xs" />,
      iconBg: "bg-slate-700",
      hasData: true,
      account: currentOrderBank,
      desc:
        selectedPayoutSource === "custom" && (currentOrderBank.name || currentOrderBank.accNo)
          ? `${currentOrderBank.name || "ไม่ระบุธนาคาร"} • เลขบัญชี: ${currentOrderBank.accNo || "-"} • ${currentOrderBank.accName || "-"}`
          : "กรอกข้อมูลบัญชีธนาคารเฉพาะสำหรับคำสั่งซื้อนี้",
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <SectionTitle title="บัญชีรับเงินรางวัล" />
      {/* Bank Account Fields for Verification & Custom Editing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-slate-50/70 rounded-md border border-slate-200">
        <FormSelect
          label="ธนาคารที่รับเงิน"
          value={currentOrderBank?.name || ""}
          disabled={loading || selectedPayoutSource !== "custom"}
          onChange={(e) => {
            updateValue("bank.name", e.target.value);
            updateValue("prizeBank.name", e.target.value);
          }}
        >
          <option value="">-</option>
          {constant.bankList.map((bank) => (
            <option key={bank.bankName} value={bank.bankName}>
              {bank.bankName}
            </option>
          ))}
        </FormSelect>

        <FormInput
          label="ชื่อบัญชีรับเงินรางวัล"
          value={currentOrderBank?.accName || ""}
          disabled={loading || selectedPayoutSource !== "custom"}
          suffix={<CopyButton text={currentOrderBank?.accName} />}
          onChange={(e) => {
            setOrderInfo((prev) => ({
              ...prev,
              bank: { ...prev.bank, accName: e.target.value },
              prizeBank: { ...prev.prizeBank, accName: e.target.value },
            }));
          }}
          onBlur={(e) => {
            updateValue("bank.accName", e.target.value);
            updateValue("prizeBank.accName", e.target.value);
          }}
        />

        <FormInput
          label="เลขบัญชีรับเงินรางวัล"
          value={currentOrderBank?.accNo || ""}
          disabled={loading || selectedPayoutSource !== "custom"}
          suffix={<CopyButton text={currentOrderBank?.accNo} />}
          onChange={(e) => {
            setOrderInfo((prev) => ({
              ...prev,
              bank: { ...prev.bank, accNo: e.target.value },
              prizeBank: { ...prev.prizeBank, accNo: e.target.value },
            }));
          }}
          onBlur={(e) => {
            updateValue("bank.accNo", e.target.value);
            updateValue("prizeBank.accNo", e.target.value);
          }}
        />
      </div>
      {/* List Bar Layout */}
      <div className="flex flex-col rounded-md border border-slate-200 divide-y divide-slate-100 bg-white shadow-xs overflow-hidden">
        {payoutOptions.map((opt) => {
          const isSelected = selectedPayoutSource === opt.id;
          const isDisabled = !opt.hasData;

          return (
            <div
              key={opt.id}
              onClick={() => handleSelectSource(opt.id, opt.account, isDisabled)}
              className={`flex items-center justify-between p-3.5 transition-all ${isDisabled
                  ? "bg-slate-50 cursor-not-allowed select-none"
                  : isSelected
                    ? "bg-blue/5 border-l-4 border-l-blue cursor-pointer"
                    : "hover:bg-slate-50/80 cursor-pointer"
                }`}
            >
              {/* Left Side: Checkbox / Radio + Icon + Info */}
              <div className="flex items-center gap-3.5 min-w-0 flex-1 pr-3">
                {/* Radio Circle */}
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${isDisabled
                      ? "border-slate-300 bg-slate-100"
                      : isSelected
                        ? "border-blue bg-blue text-white shadow-2xs"
                        : "border-slate-300 bg-white"
                    }`}
                >
                  {isSelected && <FaCheck className="text-[7px]" />}
                </div>

                {/* Color Badge / Icon */}
                <div
                  className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 shadow-2xs ${isDisabled ? "bg-slate-200 text-slate-400" : `${opt.iconBg} text-white`
                    }`}
                >
                  {opt.icon}
                </div>

                {/* Title and Description */}
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`text-xs font-bold ${isDisabled
                          ? "text-slate-400"
                          : isSelected
                            ? "text-blue font-bold"
                            : "text-slate-800"
                        }`}
                    >
                      {opt.title}
                    </span>
                    {isSelected && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue text-white shadow-2xs">
                        ใช้งานอยู่
                      </span>
                    )}
                    {isDisabled && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-slate-200/80 text-slate-500">
                        ไม่มีข้อมูลในโปรไฟล์
                      </span>
                    )}
                  </div>
                  <p
                    className={`text-[11px] mt-0.5 truncate ${isDisabled
                        ? "text-slate-400"
                        : isSelected
                          ? "text-slate-700 font-semibold"
                          : "text-slate-500 font-medium"
                      }`}
                  >
                    {opt.desc}
                  </p>
                </div>
              </div>

              {/* Right Side: Chevron Indicator */}
              <div
                className={`text-xs shrink-0 pl-2 transition-transform ${isSelected ? "text-blue translate-x-0.5" : "text-slate-300"
                  }`}
              >
                <FaChevronRight />
              </div>
            </div>
          );
        })}
      </div>


    </div>
  );
};

